import * as Yup from 'yup';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { useSnackbar } from 'notistack';
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';
import { dispatch } from '../../../redux/store';
import { login } from '../../../redux/slices/user';
import useLocales from '../../../hooks/useLocales';
// ----------------------------------------------------------------------

export default function LoginForm() {
  const isMountedRef = useIsMountedRef();
  const { translate } = useLocales();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    userName: Yup.string().required(translate('FORM.VALIDATION.LOGIN')),
    password: Yup.string().required(translate('FORM.VALIDATION.PASSWORD')),
  });

  const defaultValues = {
    userName: '',
    password: '',
    remember: true,
    afterSubmit: ''
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = async (data: any) => {
    try {
      await dispatch(login(data.userName, data.password));
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(translate(`ERRORS.${error.message}`), { variant: 'error' });
      reset();
      if (isMountedRef.current) {
        setError('afterSubmit', error);
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{translate(`ERRORS.${errors.afterSubmit.message}`) || translate('GLOBAL.SOMETHING_WENT_WRONG')}</Alert>}

        <RHFTextField name="userName" label={translate('FORM.LOGIN')} />

        <RHFTextField
          name="password"
          label={translate('FORM.PASSWORD')}
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify sx={{}} icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <RHFCheckbox name="remember" label={translate('AUTHENTICATION.STAY_LOGGED_IN')} />
        <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}>
          {translate('AUTHENTICATION.FORGOT_PASSWORD')}
        </Link>
      </Stack>

      <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
        {translate('AUTHENTICATION.ENTER')}
      </LoadingButton>
    </FormProvider>
  );
}
